import React, { useMemo, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, IconButton, Typography, Button, Card, CardContent, Divider, Stack } from '@mui/material';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { atom_tourState, atom_originalTourState } from '@app/shared/state/tour.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_bookmarkedStopsState, atom_allBookmarksState } from '@app/shared/state/filter.atom';
import { atom_listModeState, atom_ctaPanelHeight } from '@app/shared/state/list-mode.atom';
import { atom_guideRoute } from '@app/shared/state/route-tracker.atom';
import { useTranslation } from 'react-i18next';
import { Stop } from '@app/shared/models/tour.type';
import { service_tourService } from '@app/shared/services/tours.service';
import giftGIF from '@app/assets/giftGIF.gif';

// Map icon names to FontAwesome classes
const CTA_ICON_MAP: Record<string, string> = {
	gift: 'fa-solid fa-gift',
	info: 'fa-solid fa-circle-info',
	star: 'fa-solid fa-star',
	heart: 'fa-solid fa-heart',
	bell: 'fa-solid fa-bell',
	map: 'fa-solid fa-map-marker-alt',
	compass: 'fa-solid fa-compass',
	camera: 'fa-solid fa-camera',
	ticket: 'fa-solid fa-ticket',
	coffee: 'fa-solid fa-mug-hot',
	food: 'fa-solid fa-utensils',
	warning: 'fa-solid fa-triangle-exclamation',
	check: 'fa-solid fa-circle-check',
	discount: 'fa-solid fa-percent',
	time: 'fa-solid fa-clock',
	walk: 'fa-solid fa-person-walking',
};

interface CTAStopCardProps {
	stop: Stop;
	tourColor: string;
	tourId: string;
	isHebrew: boolean;
	onCardClick: () => void;
}

const CTAStopCard = React.memo(function CTAStopCard({
	stop,
	tourColor,
	tourId,
	isHebrew,
	onCardClick,
}: CTAStopCardProps) {
	const { t } = useTranslation();
	const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
	const [allBookmarks, setAllBookmarks] = useRecoilState(atom_allBookmarksState);

	const isBookmarked = bookmarkedStops.includes(stop.id);
	const stopImage = stop.loc_pics && stop.loc_pics.length > 0 ? stop.loc_pics[0].url : null;

	const handleBookmarkClick = async (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!tourId) return;

		const currentTourBookmarks = [...bookmarkedStops];
		const isAdding = !currentTourBookmarks.includes(stop.id);

		if (isAdding) {
			currentTourBookmarks.push(stop.id);
		} else {
			const index = currentTourBookmarks.indexOf(stop.id);
			if (index > -1) {
				currentTourBookmarks.splice(index, 1);
			}
		}

		setAllBookmarks({
			...allBookmarks,
			[tourId]: currentTourBookmarks,
		});

		const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
		if (uID) {
			if (isAdding) {
				await service_tourService.saveSavedStop(uID, tourId, stop.id);
			} else {
				await service_tourService.deleteSavedStop(uID, tourId, stop.id);
			}
		}
	};

	return (
		<Card
			elevation={3}
			onClick={onCardClick}
			sx={{
				display: 'flex',
				padding: '8px',
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: '8px',
				alignSelf: 'stretch',
				borderRadius: '8px',
				background: '#FFF',
				boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.15)',
				cursor: 'pointer',
				transition: 'background-color 0.2s',
				marginBottom: 2,
				border: `1px solid ${tourColor}`,
			}}
		>
			<CardContent
				sx={{
					pt: '8px',
					pb: '4px',
					px: '8px',
					width: '100%',
					height: 'auto',
					flex: 1,
				}}
			>
				<Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
					<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
						<Typography
							variant="subtitle1"
							component="h3"
							sx={{
								fontWeight: 'bold',
								color: 'rgba(31,49,59,1)',
								lineHeight: 1.7,
							}}
						>
							{stop.s_title}
						</Typography>
						{stop.s_smallDesc && (
							<Typography
								variant="body2"
								color="text.primary"
								sx={{
									mt: '6px',
									display: '-webkit-box',
									overflow: 'hidden',
									wordBreak: 'break-word',
									WebkitBoxOrient: 'vertical',
									WebkitLineClamp: 2,
									lineHeight: 1.5,
								}}
							>
								{stop.s_smallDesc}
							</Typography>
						)}
						{stop.tags && stop.tags.length > 0 && (
							<Stack
								direction="row"
								alignItems="center"
								sx={{ mt: 'auto', flexWrap: 'wrap', gap: '4px', paddingTop: '5px' }}
							>
								{stop.tags.slice(0, 2).map((tag, index) => (
									<React.Fragment key={tag.text + index}>
										<Typography
											variant="body2"
											component="span"
											sx={{
												fontWeight: 700,
												fontSize: '12px',
												lineHeight: '16px',
											}}
										>
											{tag.text}
										</Typography>
										{index < Math.min(stop.tags.length, 2) - 1 && (
											<Box
												sx={{
													bgcolor: '#48526B',
													width: '3px',
													height: '3px',
													borderRadius: '50%',
													ml: '5px',
													mr: '5px',
												}}
											/>
										)}
									</React.Fragment>
								))}
							</Stack>
						)}
					</Box>
					{stopImage && (
						<img
							src={stopImage}
							alt={stop.s_title}
							style={{
								width: 82,
								height: 82,
								objectFit: 'cover',
								borderRadius: 4,
							}}
						/>
					)}
				</Box>
			</CardContent>

			<Divider
				variant="fullWidth"
				sx={{ width: '100%', borderColor: '#D5D9E2', borderBottomWidth: '1.5px' }}
			/>

			<Box sx={{ px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
				<Button
					variant="text"
					sx={{
						color: '#000',
						fontWeight: 600,
						backgroundColor: 'transparent',
						fontSize: 14,
						px: 0,
						minWidth: 'auto',
						gap: '6px',
						textTransform: 'none',
					}}
				>
					{t('new_app.explore_more')}
					<i
						className={isHebrew ? 'fa-solid fa-angle-left' : 'fa-solid fa-angle-right'}
						aria-hidden="true"
						style={{ color: '#000', fontSize: 24, fontWeight: 300 }}
					/>
				</Button>
				<IconButton
					onClick={handleBookmarkClick}
					size="small"
					aria-label={(isBookmarked ? t('accessibility.remove_from_saved') : t('accessibility.add_to_saved')) as string}
				>
					<i
						className={isBookmarked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'}
						style={{ color: isBookmarked ? tourColor : 'black', fontSize: 20 }}
					/>
				</IconButton>
			</Box>
		</Card>
	);
});

interface MobileCTAContentProps {
	contentRef?: React.RefObject<HTMLDivElement>;
}

export const MobileCTAContent = React.memo(function MobileCTAContent({
	contentRef,
}: MobileCTAContentProps) {
	const { t, i18n } = useTranslation();
	const { activeCTA, dismissCTA, markAsSeen } = useNotificationTrigger();
	const tourState = useRecoilValue(atom_tourState);
	const setCtaPanelHeight = useSetRecoilState(atom_ctaPanelHeight);
	const ctaPanelRef = useRef<HTMLDivElement | null>(null);
	const originalTour = useRecoilValue(atom_originalTourState);
	const setOpenedStop = useSetRecoilState(atom_openedStopState);
	const setActiveStop = useSetRecoilState(atom_activeStopState);
	const setListMode = useSetRecoilState(atom_listModeState);
	const guideRoute = useRecoilValue(atom_guideRoute);

	// Get tour color or fallback
	const tourColor = tourState?.config?.mainColor || '#E65100';

	const isHebrew = () => i18n.language === 'he';

	const isNavCTA = activeCTA?.id?.startsWith('nav-') ?? false;
	// nav-start CTAs render OpeningStopBlock-style (centered image + single Skip), not the stop-picker UI.
	const isNavStartCTA = activeCTA?.id?.startsWith('nav-start-') ?? false;

	// For navigator CTAs, use guide route stop order if available, otherwise
	// fall back to the tour's own stops sorted by `order`. atom_originalTourState
	// is effectively dead in most flows, so read tourState directly.
	const orderedStops = useMemo(() => {
		if (!isNavCTA || !tourState?.stops) return [];
		if (guideRoute?.stopOrder && guideRoute.stopOrder.length > 0) {
			const mapped = guideRoute.stopOrder
				.map((id) => tourState.stops.find((s) => s.id === id))
				.filter((s): s is Stop => !!s);
			if (mapped.length > 0) return mapped;
		}
		return [...tourState.stops]
			.filter((s) => s.include_path !== false)
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
	}, [isNavCTA, tourState?.stops, guideRoute?.stopOrder]);

	const initialNavIndex = useMemo(() => {
		if (!isNavCTA || !activeCTA?.ctaConfig?.actionValue || orderedStops.length === 0) return 0;
		const idx = orderedStops.findIndex((s) => s.id === activeCTA.ctaConfig?.actionValue);
		return idx >= 0 ? idx : 0;
	}, [isNavCTA, activeCTA?.ctaConfig?.actionValue, orderedStops]);

	const [navStopIndex, setNavStopIndex] = useState(initialNavIndex);

	// Reset index when CTA changes
	useEffect(() => {
		setNavStopIndex(initialNavIndex);
	}, [initialNavIndex]);

	const navStop = orderedStops[navStopIndex] ?? null;
	const hasPrevStop = navStopIndex > 0;
	const hasNextStop = navStopIndex < orderedStops.length - 1;

	// Find stop if actionType is navigate, modal, or openStop (actionValue = stop-id).
	// nav-start CTAs participate (resolve their first stop), but other nav-CTAs still use their own picker.
	// nav-start uses tourState (originalTour is dead in most flows); regular CTAs keep originalTour.
	const linkedStop = useMemo(() => {
		if (isNavCTA && !isNavStartCTA) return null;
		const actionType = activeCTA?.ctaConfig?.actionType;
		const actionValue = activeCTA?.ctaConfig?.actionValue;

		if (!actionValue) return null;
		if (actionType !== 'navigate' && actionType !== 'modal' && actionType !== 'openStop') return null;

		const stops = isNavStartCTA ? tourState?.stops : originalTour?.stops;
		if (!stops) return null;

		return stops.find(stop => stop.id === actionValue);
	}, [isNavCTA, isNavStartCTA, activeCTA?.ctaConfig?.actionType, activeCTA?.ctaConfig?.actionValue, tourState?.stops, originalTour?.stops]);

	// Set active stop when linkedStop is found
	useEffect(() => {
		if (linkedStop) {
			setActiveStop(linkedStop);
		}
	}, [linkedStop, setActiveStop]);

	// Measure panel height and publish to atom_ctaPanelHeight so fly helpers can
	// account for the panel in their bottom padding. Reset to 0 when the CTA is
	// dismissed or the component unmounts.
	useEffect(() => {
		if (!activeCTA) {
			setCtaPanelHeight(0);
			return;
		}
		const node = ctaPanelRef.current;
		if (!node || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver((entries) => {
			const h = Math.round(entries[0]?.contentRect?.height ?? node.offsetHeight ?? 0);
			setCtaPanelHeight(h);
		});
		observer.observe(node);
		return () => {
			observer.disconnect();
			setCtaPanelHeight(0);
		};
	}, [activeCTA, setCtaPanelHeight]);

	const ctaConfig = activeCTA?.ctaConfig;

	// Handle CTA button click based on action type
	// Primary button only performs action, does NOT dismiss or mark as seen
	const handleCTAClick = () => {
		if (!activeCTA || !ctaConfig) return;

		switch (ctaConfig.actionType) {
			case 'link':
				if (ctaConfig.actionValue) {
					window.open(ctaConfig.actionValue, '_blank');
				}
				break;
			case 'navigate':
				if (linkedStop) {
					setListMode('mobileNavigation');
				}
				break;
			case 'modal':
			case 'openStop':
				if (linkedStop) {
					setOpenedStop(linkedStop);
				}
				break;
			default:
				break;
		}
		// Track that user clicked the CTA (but don't dismiss)
		markAsSeen(activeCTA.id, 'ctaClicked');
	};

	// Handle stop card click - same as primary button, only performs action
	const handleStopCardClick = () => {
		if (!activeCTA || !linkedStop) return;
		setOpenedStop(linkedStop);
		markAsSeen(activeCTA.id, 'ctaClicked');
	};

	// Handle navigator stop option click - navigate to chosen stop
	const handleNavStopClick = (stop: Stop) => {
		if (!activeCTA) return;
		setActiveStop(stop);
		setListMode('mobileNavigation');
		markAsSeen(activeCTA.id, 'ctaClicked');
		dismissCTA();
	};

	// nav-start "Take me there": same flow as the direction button on stop cards on mobile —
	// activate the first stop, switch list to mobileNavigation (which renders the Walk/Bus/Bike/Car chooser),
	// then dismiss this CTA so it doesn't reappear behind the chooser.
	const handleNavStartPrimary = () => {
		if (!activeCTA || !linkedStop) return;
		setActiveStop(linkedStop);
		setListMode('mobileNavigation');
		markAsSeen(activeCTA.id, 'ctaClicked');
		dismissCTA();
	};

	// Handle skip/secondary button - marks as seen and dismisses
	const handleSkip = () => {
		if (!activeCTA) return;
		markAsSeen(activeCTA.id, 'seen');
		dismissCTA();
	};

	return (
		<AnimatePresence mode="wait">
			{activeCTA && (
				<motion.div
					key={activeCTA.id}
					ref={(node: HTMLDivElement | null) => {
						ctaPanelRef.current = node;
						if (contentRef) {
							(contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
						}
					}}
					initial={{ y: '100%' }}
					animate={{ y: 0 }}
					exit={{ y: '100%' }}
					transition={{ type: 'spring', damping: 30, stiffness: 150 }}
					style={{
						position: 'fixed',
						bottom: 0,
						left: 0,
						right: 0,
						zIndex: 9999,
					}}
				>
					<Box
						sx={{
							padding: '12px 12px 80px 12px',
							backgroundColor: '#fff',
							borderRadius: '24px 24px 0 0',
							boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
							position: 'relative',
							minHeight: '30vh',
						}}
					>
						{/* Icon + Title + Content wrapper with GIF background */}
						<Box sx={{ position: 'relative', pb: 3, clipPath: 'inset(-100% -100% 0 -100%)' }}>
							{/* Gift GIF background - plays once on CTA display */}
							{activeCTA.icon === 'gift' && (
								<Box
									sx={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										zIndex: 0,
										pointerEvents: 'none',
									}}
								>
									<img
										key={`gift-gif-${activeCTA.id}`}
										src={giftGIF}
										alt=""
										aria-hidden="true"
										style={{
											width: 400,
											height: 280,
											objectFit: 'contain',
										}}
									/>
								</Box>
							)}

							{/* Icon */}
							{activeCTA.icon && CTA_ICON_MAP[activeCTA.icon] && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										marginBottom: 2,
										position: 'relative',
										zIndex: 1,
									}}
								>
									<Box
										sx={{
											...(activeCTA.id?.startsWith('nav-')
												? { width: '72px', height: '72px' }
												: { padding: '25px' }
											),
											borderRadius: '50%',
											backgroundColor: `${tourColor}20`,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<i
											className={CTA_ICON_MAP[activeCTA.icon]}
											style={{
												fontSize: activeCTA.id?.startsWith('nav-') ? '28px' : '32px',
												color: tourColor,
											}}
										/>
									</Box>
								</Box>
							)}

							{/* Title */}
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									fontSize: '18px',
									lineHeight: '24px',
									letterSpacing: '0px',
									textAlign: 'center',
									color: '#333',
									marginBottom: '10px',
									paddingX: 4,
									wordBreak: 'keep-all',
									overflowWrap: 'break-word',
									position: 'relative',
									zIndex: 1,
								}}
							>
								{activeCTA.title}
							</Typography>

							{/* Content */}
							<Typography
								component="div"
								variant="body2"
								sx={{
									fontWeight: 400,
									fontSize: '16px',
									lineHeight: '24px',
									letterSpacing: '0px',
									textAlign: 'center',
									color: '#666',
									marginBottom: '10px',
									paddingX: 2,
									wordBreak: 'keep-all',
									overflowWrap: 'break-word',
									'& p': { margin: 0 },
									'& strong': { fontWeight: 600 },
									position: 'relative',
									zIndex: 1,
								}}
								dangerouslySetInnerHTML={{ __html: activeCTA.content }}
							/>
						</Box>

						{/* nav-start (OpeningStopBlock-style): centered image — no stop-picker card */}
						{isNavStartCTA && activeCTA?.thumbnail && (
							<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
								<img
									src={activeCTA.thumbnail}
									alt=""
									style={{
										maxHeight: '35vh',
										maxWidth: '100%',
										borderRadius: 12,
										objectFit: 'cover',
									}}
								/>
							</Box>
						)}

						{/* Navigator CTA: first stop box with image */}
						{isNavCTA && !isNavStartCTA && navStop && (
							<Box sx={{ mb: 1 }}>
								<Box
									onClick={() => handleNavStopClick(navStop)}
									role="button"
									tabIndex={0}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										padding: '6px',
										borderRadius: '12px',
										border: `1.5px solid ${tourColor}`,
										backgroundColor: '#fff',
										cursor: 'pointer',
										transition: 'all 0.2s',
										'&:hover': {
											backgroundColor: `${tourColor}08`,
											boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
										},
									}}
								>
									{navStop.loc_pics && navStop.loc_pics.length > 0 ? (
										<img
											src={navStop.loc_pics[0].url}
											alt={navStop.s_title}
											style={{
												width: 56,
												height: 56,
												objectFit: 'cover',
												borderRadius: 8,
												flexShrink: 0,
											}}
										/>
									) : (
										<Box
											sx={{
												width: '56px',
												height: '56px',
												borderRadius: '8px',
												backgroundColor: `${tourColor}15`,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											<i
												className="fa-solid fa-location-arrow"
												style={{ fontSize: '18px', color: tourColor }}
											/>
										</Box>
									)}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="caption"
											sx={{
												color: tourColor,
												fontWeight: 600,
												fontSize: '11px',
												textTransform: 'uppercase',
												letterSpacing: '0.5px',
											}}
										>
											{t('navigator.first_stop')}
										</Typography>
										<Typography
											variant="subtitle2"
											sx={{
												fontWeight: 600,
												color: '#333',
												fontSize: '14px',
												lineHeight: '20px',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											{navStop.s_title}
										</Typography>
										{navStop.s_smallDesc && (
											<Typography
												variant="body2"
												sx={{
													color: '#666',
													fontSize: '12px',
													lineHeight: '16px',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
											>
												{navStop.s_smallDesc}
											</Typography>
										)}
									</Box>
									<i
										className={isHebrew() ? 'fa-solid fa-chevron-left' : 'fa-solid fa-chevron-right'}
										style={{ fontSize: '14px', color: '#999', flexShrink: 0 }}
									/>
								</Box>
							</Box>
						)}

						{/* Regular CTA: Stop Card */}
						{!isNavCTA && linkedStop && tourState?.id && (
							<CTAStopCard
								stop={linkedStop}
								tourColor={tourColor}
								tourId={tourState.id}
								isHebrew={isHebrew()}
								onCardClick={handleStopCardClick}
							/>
						)}

						{/* Action Buttons */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							{/* Primary Button — regular CTAs use handleCTAClick; nav-start uses the "Take me there" flow */}
							{(!isNavCTA || isNavStartCTA) && ctaConfig?.primaryButtonText && (
								<Button
									variant="contained"
									onClick={isNavStartCTA ? handleNavStartPrimary : handleCTAClick}
									sx={{
										height: '44px',
										backgroundColor: tourColor,
										color: '#fff',
										borderRadius: '20px',
										padding: '12px 24px',
										fontWeight: 600,
										textTransform: 'none',
										fontSize: '15px',
										boxShadow: 'none',
										'&:hover': {
											backgroundColor: tourColor,
											opacity: 0.9,
											boxShadow: 'none',
										},
									}}
								>
									{ctaConfig.primaryButtonText}
								</Button>
							)}

							{/* Navigator CTA: prev / skip / next row (suppressed for nav-start welcome) */}
							{isNavCTA && !isNavStartCTA && ctaConfig?.secondaryButtonText && (
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
									<Button
										variant="text"
										disabled={!hasPrevStop}
										onClick={() => {
											setNavStopIndex((i) => {
												const newIdx = i - 1;
												if (orderedStops[newIdx]) setActiveStop(orderedStops[newIdx]);
												return newIdx;
											});
										}}
										sx={{
											height: '44px',
											color: hasPrevStop ? tourColor : '#ccc',
											borderRadius: '20px',
											padding: '12px 16px',
											fontWeight: 500,
											textTransform: 'none',
											fontSize: '14px',
											minWidth: 'auto',
											gap: '4px',
											'&:hover': {
												backgroundColor: hasPrevStop ? `${tourColor}08` : 'transparent',
											},
										}}
									>
										<i
											className={isHebrew() ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-left'}
											style={{ fontSize: '12px' }}
										/>
										{t('previous')}
									</Button>
									<Button
										variant="text"
										onClick={handleSkip}
										sx={{
											height: '44px',
											color: '#666',
											borderRadius: '20px',
											padding: '12px 16px',
											fontWeight: 500,
											textTransform: 'none',
											fontSize: '14px',
											'&:hover': {
												backgroundColor: 'rgba(0,0,0,0.04)',
											},
										}}
									>
										{ctaConfig.secondaryButtonText}
									</Button>
									<Button
										variant="text"
										disabled={!hasNextStop}
										onClick={() => {
											setNavStopIndex((i) => {
												const newIdx = i + 1;
												if (orderedStops[newIdx]) setActiveStop(orderedStops[newIdx]);
												return newIdx;
											});
										}}
										sx={{
											height: '44px',
											color: hasNextStop ? tourColor : '#ccc',
											borderRadius: '20px',
											padding: '12px 16px',
											fontWeight: 500,
											textTransform: 'none',
											fontSize: '14px',
											minWidth: 'auto',
											gap: '4px',
											'&:hover': {
												backgroundColor: hasNextStop ? `${tourColor}08` : 'transparent',
											},
										}}
									>
										{t('next')}
										<i
											className={isHebrew() ? 'fa-solid fa-chevron-left' : 'fa-solid fa-chevron-right'}
											style={{ fontSize: '12px' }}
										/>
									</Button>
								</Box>
							)}

							{/* Regular CTA / nav-start welcome: single Skip/Dismiss button */}
							{(!isNavCTA || isNavStartCTA) && ctaConfig?.secondaryButtonText && (
								<Button
									variant="text"
									onClick={handleSkip}
									sx={{
										height: '44px',
										color: '#666',
										borderRadius: '20px',
										padding: '12px 24px',
										fontWeight: 500,
										textTransform: 'none',
										fontSize: '14px',
										'&:hover': {
											backgroundColor: 'rgba(0,0,0,0.04)',
										},
									}}
								>
									{ctaConfig.secondaryButtonText}
								</Button>
							)}
						</Box>
					</Box>
				</motion.div>
			)}
		</AnimatePresence>
	);
});
